import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, Routes, Route, useLocation } from 'react-router-dom';
import EntregaPDVForm from '../components/forms/EntregaPDVForm';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../contexts/AuthContext';
import { STORE_STYLES } from '../assets/data/arrays';

/** Prefijo + color de tienda (misma fuente que VerifyTransactions / PendingOrders). */
const getEntregaPdvStoreBadge = (storeName?: string | null) => {
  const name = (storeName ?? '').trim();
  const row = STORE_STYLES.find(
    (s) =>
      s.value === name
      || (name === 'Barístico' && s.value === 'Baristico')
      || (name === 'Home Center' && s.value === 'Homecenter')
  );
  if (row) {
    return { prefix: row.shortValue, className: row.color };
  }
  const parts = name.split(/\s+/).filter(Boolean);
  let prefix = '??';
  if (parts.length >= 2) {
    prefix = `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  } else if (parts.length === 1) {
    const w = parts[0];
    prefix = w.length >= 2 ? w.slice(0, 2).toUpperCase() : `${(w[0] || '?').toUpperCase()}?`;
  }
  return { prefix, className: 'text-gray-600 font-semibold' };
};

interface Order {
  id: number;
  orders_id: string;
  customer_full_name: string;
  date_created: string;
  store_name: string;
  status: string;
  total: string;
  payment_method?: string;
  transaction_id?: string;
  extraDetails?: {
    payment_method?: string;
    transaction_id?: string;
    recoge_en_bodega?: number;
  };
}

interface OrderDetail {
  id: number;
  orders_id: number;
  origin_order_id: number;
  store_name: string;
  status: string;
  total: string;
  guia?: string;
  date_created: string;
  date_modified: string;
  agente?: number;
  customer: { 
    first_name: string; 
    last_name: string; 
    email: string; 
    document?: string; 
    tipo_documento?: string;
  };
  billingAddress: { 
    address_1: string; 
    city: string; 
    state: string; 
    country: string; 
    postcode: string; 
    phone?: string;
  };
  shippingAddress: { 
    address_1: string; 
    city: string; 
    state: string; 
    country: string; 
    postcode: string; 
    phone?: string;
  };
  lineItems: Array<{ 
    product_id: string; 
    product_name: string; 
    quantity: number; 
    price: string;
  }>;
}

const EntregaPDV: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  
  const lastOrderElementRef = useCallback((node: HTMLDivElement) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore, isLoadingMore]);

  const fetchEntregaPDVOrders = useCallback(async (pageNumber: number) => {
    try {
      setIsLoadingMore(true);
      console.log('🔍 Rol actual del usuario:', user?.role_id);
      console.log('🔍 Usuario completo:', user);
      const token = localStorage.getItem('accessToken');
      
      // Buscar pedidos con estado "enpdv"
      const response = await axiosInstance.get(`/list?&limit=20&page=${pageNumber}&states=enpdv&id_market=95&verTodos=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // No necesitamos filtrar adicionalmente, ya que el backend filtra por estado "enpdv"
      const filteredOrders = response.data.data;

      console.log(`📊 Total pedidos en estado "enpdv": ${filteredOrders.length}`);
      
      const formattedOrders = filteredOrders.map((order: Order) => ({
        ...order,
        date_created: new Date(order.date_created).toLocaleString(),
      }));

      if (pageNumber === 1) {
        setOrders(formattedOrders);
      } else {
        setOrders(prevOrders => [...prevOrders, ...formattedOrders]);
      }

      setHasMore(formattedOrders.length === 20);
    } catch (error) {
      console.error('Error fetching entrega PDV orders:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [user]);

  useEffect(() => {
    // Solo ejecutar si el usuario está cargado
    if (user) {
      fetchEntregaPDVOrders(page);
    }
  }, [page, fetchEntregaPDVOrders, user]);

  const handleOrderClick = async (orderId: number) => {
    try {
      const response = await axiosInstance.get(`/list/${orderId}`);
      setSelectedOrder(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  return (
    <div className="flex h-full">
      <div className="w-48 bg-white border-r border-gray-200 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        <div className="p-4">
          <h4>Lista de Entregas PDV</h4>
          {isLoading && page === 1 ? (
            <div className="text-center text-gray-500">Cargando pedidos...</div>
          ) : (
            <div className="space-y-2">
              {orders.map((order, index) => {
                const badge = getEntregaPdvStoreBadge(order.store_name);
                return (
                <div key={order.id}
                  ref={index === orders.length - 1 ? lastOrderElementRef : null}>
                  <Link to={`/orders/entrega-pdv/${order.id}`}
                    onClick={() => handleOrderClick(order.id)}
                    className={`text-sm block p-3 rounded-lg transition-colors ${
                      location.pathname === `/orders/entrega-pdv/${order.id}`
                      ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'
                    }`}>
                    <div className="font-semibold">
                      <span className={badge.className}>
                        {' '}{badge.prefix}
                      </span> #{order.id}
                    </div>
                    <div className="text-xs text-gray-500">{order.customer_full_name}</div>
                    <div className="text-xs text-gray-400">{order.date_created}</div>
                  </Link>
                </div>
                );
              })}
              {isLoadingMore && (
                <div className="text-center text-gray-500 py-2">Cargando más pedidos...</div>
              )}
              {!isLoading && orders.length === 0 && (
                <div className="text-center text-gray-500">No hay pedidos en estado "Entrega PDV"</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        <Routes>
          <Route path="/" element={<EntregaPDVForm />} />
          <Route path="/:id" element={<EntregaPDVForm orderDetail={selectedOrder} />} />
        </Routes>
      </div>
    </div>
  );
};

export default EntregaPDV;
